/**
 * Where a floating panel lands. One rule, for every panel that drops from a
 * control.
 *
 * EVERY FLOATING PANEL IN `web/` IS PORTALED TO `<body>` AND PLACED IN
 * PHYSICAL VIEWPORT COORDINATES. That is not a style: a panel rendered inside
 * a scrolling table cell is clipped by it, and one positioned with logical CSS
 * is thrown to the mirrored side of the screen under `dir=rtl`, because
 * `insetInlineStart` reads a physical `left` as a `right` offset. So the mirror
 * happens in the arithmetic and the stylesheet stays physical.
 *
 * That contract was agreed by five components and implemented five times, each
 * with its own constants — margins of 8 and 16, `margin`/`gap` pairs, and a
 * vertical rule that two of them had and three did not. A panel near the bottom
 * of a long table therefore ran off the screen or scrolled or flipped depending
 * on which panel it was, and the flip defect had to be found and fixed once per
 * component. This is the single owner of the mirror, the edge clamp and the
 * flip.
 *
 * WHAT IS NOT HERE: `LabTrendTooltip` places a tooltip on one of FOUR sides of
 * a point on a chart and flips between opposite sides. That is a different
 * geometry, not a configuration of this one, and folding it in would mean a
 * boolean per difference — so it keeps its own four-sided rule and shares the
 * edge clamp below, which is the part the two genuinely have in common.
 *
 * `AppearanceMenu` and `NotificationBell` were the last two out, hanging from
 * the trigger's reading-END edge and OVERHANGING the bottom of the screen
 * rather than lifting off it — a decision argued in `AppearanceMenu.test.tsx`
 * at a time when flipping was not among the options either component had, so
 * the pinned comparison was overhang against a 26px sliver and flipping never
 * entered it. The fork was decided on 2026-08-30, for flipping and for one
 * rule: a panel that flips above its trigger is whole and scrollable where an
 * overhung one keeps its tail off screen, and an option per component is how
 * five copies became five contracts in the first place. What those two
 * genuinely needed — hanging from the reading-END edge — is the `align` input
 * below: geometry, like width, not a behaviour.
 *
 * Every panel `web/` portals out is placed by `placeDropPanel`.
 *
 * WIDTH IS A UNIT, NOT A BEHAVIOUR, which is why it is asked for two ways
 * below rather than one. A panel is sized either by its own stylesheet or by
 * the answer this helper gives, never by both, and the caller states which:
 * `widthRem` for the first, `measuredWidth` for the second. That is not an
 * option per component — both doors run the same arithmetic, and what the
 * caller picks is which number is the ORIGINAL one. A bare pixel width was the
 * third, unstated shape: it read as safe beside a `rem` rule and was wrong at
 * three of the four reading scales, so it is gone from the type and no longer
 * compiles. `panelWidthFromRem` is exported beside it for the panels that
 * place themselves and still need the same conversion.
 */
import { readingDirectionIsRtl } from './readingDirection'

/** Clear of the viewport border, on every edge, for every panel. */
export const VIEWPORT_MARGIN = 8

/**
 * The shortest a panel is worth opening at: 6rem, converted per placement.
 * Below this it is a sliver that can hold no row, so the panel is pulled up
 * off the edge instead of being drawn where it was asked for. A "row" is a
 * reading measure — at 110% scale each row is taller, and a threshold frozen
 * at 96px would call a two-row sliver worth opening.
 */
const MIN_PANEL_REM = 6

/**
 * Room worth having below a trigger, in rem, for a panel that cannot be shown
 * whole on either side. Moving a menu to the other side of its own button is
 * disorienting, so a panel that must scroll wherever it goes stays put while
 * what is left below is still worth reading, and lifts only out of a sliver.
 * "Worth reading" is rows of text, so the threshold follows the dial too.
 *
 * IT IS THE TIE-BREAK, NOT THE RULE. A SCROLLBAR IS THE LAST RESORT: when the
 * panel fits above and does not fit below, it moves — a panel scrolling inside
 * 300px with 560 free above it is the reader paying for the placement's
 * convenience, and the calendar's object inspector is where that was found.
 */
const COMFORTABLE_REM = 13.75

/**
 * Hold one coordinate between two viewport edges.
 *
 * THE LOWER BOUND WINS A TIE. When the panel is wider than the viewport can
 * hold, the upper bound falls below the lower one and a bare `Math.min` of a
 * `Math.max` would pin the panel to a NEGATIVE offset — off the leading edge,
 * which is the one the reader starts from.
 */
export function clampToViewport(preferred: number, size: number, extent: number): number {
  return Math.min(
    Math.max(preferred, VIEWPORT_MARGIN),
    Math.max(VIEWPORT_MARGIN, extent - size - VIEWPORT_MARGIN),
  )
}

/**
 * What `inline-size: min(<n>rem, calc(100vw - 16px))` evaluates to right now.
 *
 * A panel sized by a stylesheet has two numbers behind it: the `rem` the rule
 * states and the pixel count the placement arithmetic clamps against. THE
 * READING-SCALE DIAL MOVES THE ROOT FONT SIZE — `SCALES = [85, 90, 100, 110]`
 * in `shared/appearance/appearance.ts`, so 13.6px to 17.6px — which makes a
 * pixel constant written beside a `rem` rule correct at exactly one setting.
 * At 110% the panel renders wider than the placement believes and spends the
 * difference off the reading edge, taking whatever controls sit at that end
 * with it, and it does so worst for the reader who asked for larger text.
 *
 * So there is one number to keep — the `rem` the stylesheet states — and the
 * pixels are derived where they are needed.
 *
 * THE NARROW-VIEWPORT ARM IS NOT TYPE-RELATIVE, and the stylesheets say `16px`
 * rather than `1rem` for it. That arm is the viewport clearance, which is
 * `VIEWPORT_MARGIN` on each edge and belongs to the placement rather than to
 * the reading measure. Written as a `rem` the two agreed at 100% and nowhere
 * else: below it the stylesheet drew a panel WIDER than the clamp had been
 * computed for, so the panel crossed on one side the margin it was being kept
 * clear of on the other.
 *
 * The fallback is 16 rather than a throw. A root font size this cannot parse
 * means a panel a few pixels off; refusing to place it means no panel at all.
 */
export function panelWidthFromRem(rem: number): number {
  return Math.min(rem * rootFontPx(), window.innerWidth - VIEWPORT_MARGIN * 2)
}

/**
 * What a `rem` height measures right now. The vertical twin of the conversion
 * above, for the height caps and floors the placement takes and applies —
 * there is no viewport arm here because vertical room is what `placeDropPanel`
 * itself divides, per side, when it places.
 *
 * A height stated to this helper follows the reading-scale dial the way every
 * stylesheet `rem` does. The px constants this replaced — a 340px form, a
 * 440px option list — held five rows at one scale and four and a half at the
 * next, and each was the number `maxHeight` genuinely applied, so the drift
 * was real geometry, not telemetry.
 */
export function panelHeightFromRem(rem: number): number {
  return rem * rootFontPx()
}

/**
 * WHAT ONE `rem` MEASURES RIGHT NOW, in pixels — the one reading of the root
 * font size in `web/`.
 *
 * The reading scale moves it, so it is read on every conversion rather than
 * cached: a clinician can turn the dial with a panel open, and a held value
 * would convert the next answer against the previous scale.
 *
 * It is exported because the conversion has a second direction. The panel
 * resizer divides by it — a drag is pixels and the stored width is `rem` — and
 * it kept its own copy of this function, so a correction made here (a clamp, a
 * rounding rule, a different fallback) would have left the dragged width and
 * the stylesheet's width disagreeing at every scale but the default.
 *
 * The fallback is 16 rather than a throw. A root font size this cannot parse
 * means a panel a few pixels off; refusing to answer means no panel at all.
 */
export function rootFontPx(): number {
  const root = Number.parseFloat(getComputedStyle(document.documentElement).fontSize)
  return Number.isFinite(root) && root > 0 ? root : 16
}

/**
 * A panel whose width lives in ITS STYLESHEET, stated in `rem`. Pass the same
 * number the rule states and nothing else; the pixels are derived above.
 */
interface StylesheetWidth {
  widthRem: number
  measuredWidth?: never
}

/**
 * A panel that APPLIES the width this helper answers with, and whose
 * stylesheet therefore states none. Measured content and floor widths belong
 * here, and a pixel number is safe in this form precisely because it is the
 * only source of the width there is.
 */
interface AppliedWidth {
  measuredWidth: number
  widthRem?: never
}

interface DropPanelGeometry {
  /** The control the panel drops from, in physical viewport coordinates. */
  anchor: DOMRect
  /** Space between the panel and the control it belongs to. */
  gap: number
  /**
   * The tallest this panel should ever be, when it has an answer. A panel
   * sized by its own measured content passes that measurement; one that
   * scrolls inside itself passes its cap; one that has neither passes nothing
   * and takes whatever the viewport leaves.
   */
  height?: number
  /**
   * Whether the panel may open ABOVE its trigger when there is no room below.
   * A panel that scrolls inside itself may prefer to stay under the control
   * that opened it — near the bottom edge that is a short scroll, not a lost
   * action. A panel whose content cannot scroll must flip or be lost.
   */
  flip?: boolean
  /**
   * Which of the anchor's edges the panel hangs from. `start` — the default —
   * aligns the panel's reading-start edge with the anchor's; `end` aligns the
   * reading-END edges, which is how header chrome whose trigger sits at the
   * end of the bar hangs its panel back over the content instead of off the
   * screen. Geometry, like width, not a behaviour: both edges run the same
   * mirror and the same clamp.
   */
  align?: 'start' | 'end'
  /**
   * Whether the panel will APPLY the height it is given. Almost all of them do,
   * and shortening one to the room available is what keeps it on screen.
   *
   * A panel that says `false` is drawn at its own measured height whatever this
   * answers — a menu of three languages, say — so the height must not be
   * shortened behind its back: doing that positions a short panel and renders a
   * tall one, and the difference falls off the bottom of the viewport. Such a
   * panel is placed by its FULL height instead, clamped between both edges.
   */
  scrolls?: boolean
}

export type DropPanelRequest =
  | (DropPanelGeometry & StylesheetWidth)
  | (DropPanelGeometry & AppliedWidth)

export interface DropPanelPlacement {
  left: number
  top: number
  /** The requested width, after the viewport clamp. */
  width: number
  /** What the panel may occupy on the side it chose. Apply it, or scroll. */
  maxHeight: number
  opensAbove: boolean
}

export function placeDropPanel(request: DropPanelRequest): DropPanelPlacement {
  const { anchor, gap, height, flip = true, scrolls = true, align = 'start' } = request
  const margin = VIEWPORT_MARGIN
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  // The one place the two doors meet. A stylesheet-sized panel is converted
  // through the live root font size; a panel that applies this answer states
  // its pixels directly, because for that one there is nothing to disagree
  // with.
  const wantedWidth =
    request.widthRem === undefined ? request.measuredWidth : panelWidthFromRem(request.widthRem)
  const panelWidth = Math.min(wantedWidth, viewportWidth - margin * 2)
  // The mirror. Under `dir=rtl` the reading start is the trigger's RIGHT edge
  // and the reading end its LEFT, so both alignments swap their physical
  // sides; the clamp then keeps the panel on screen either way.
  const rtl = readingDirectionIsRtl()
  const startEdge = rtl ? anchor.right - panelWidth : anchor.left
  const endEdge = rtl ? anchor.left : anchor.right - panelWidth
  const left = clampToViewport(
    align === 'end' ? endEdge : startEdge,
    panelWidth,
    viewportWidth,
  )

  const below = viewportHeight - anchor.bottom - gap - margin
  const above = anchor.top - gap - margin
  const wanted = height ?? Number.POSITIVE_INFINITY
  // Staying below is the default and needs no reason; leaving it does. There
  // are two: the panel does not fit below but fits WHOLE above, or nothing
  // fits anywhere and what is left below is a sliver. A caller that states no
  // height wants what it can get, and only the second reason can reach it.
  const opensAbove =
    flip &&
    above > below &&
    wanted > below &&
    (wanted <= above || below < panelHeightFromRem(COMFORTABLE_REM))

  if (!scrolls) {
    // Placed by what it will actually be. `wanted` is finite here: a panel that
    // neither scrolls nor states a height has no height anyone can place it by.
    return {
      left,
      top: clampToViewport(
        opensAbove ? anchor.top - gap - wanted : anchor.bottom + gap,
        wanted,
        viewportHeight,
      ),
      width: panelWidth,
      maxHeight: wanted,
      opensAbove,
    }
  }

  const available = opensAbove ? above : below
  const maxHeight = Math.max(panelHeightFromRem(MIN_PANEL_REM), Math.min(wanted, available))

  return {
    left,
    top: opensAbove
      ? Math.max(margin, anchor.top - gap - maxHeight)
      : Math.max(margin, Math.min(anchor.bottom + gap, viewportHeight - maxHeight - margin)),
    width: panelWidth,
    maxHeight,
    opensAbove,
  }
}

/**
 * A panel that stands BESIDE its anchor rather than under it: the hint and the
 * hover card the navigation rail raises next to one of its glyphs, or the
 * same card dropped BELOW the glyph once the rail lies along the top of a
 * stacked page. It is the second geometry this module owns, and it is here
 * rather than in the rail's own module for the reason the first one is: the
 * mirror, the clamp and the `rem` door are one decision, not one per caller.
 *
 * `side` is LOGICAL. `inline-end` puts the panel past the anchor's reading-end
 * edge — its physical right under `ltr` and its left under `rtl` — with the
 * panel's top on the anchor's top; `block-end` hangs it under the anchor with
 * the reading-start edges aligned, which is `placeDropPanel`'s geometry
 * without the flip, because a strip pinned to the top of a page has no room
 * above it for a panel to flip into. Both are clamped inside the viewport on
 * both axes, so a glyph at the bottom of a tall rail still gets its whole
 * card.
 */
export interface SidePanelRequest {
  anchor: DOMRect
  gap: number
  side: 'inline-end' | 'block-end'
  /** The panel's own measured size: it applies none of this answer but its place. */
  width: number
  height: number
}

export interface SidePanelPlacement {
  left: number
  top: number
  /**
   * The most the panel may stand tall: the viewport between the two margins
   * the clamp keeps. A card taller than that — a long recently-opened tray —
   * scrolls inside itself rather than running off the bottom, and the number
   * is here, beside the margin it is made of, rather than restated in a
   * stylesheet as a viewport unit the reading scale cannot see.
   */
  maxHeight: number
}

export function placeSidePanel(request: SidePanelRequest): SidePanelPlacement {
  const { anchor, gap, side, width, height } = request
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  const rtl = readingDirectionIsRtl()
  const maxHeight = Math.max(0, viewportHeight - 2 * VIEWPORT_MARGIN)
  const fitted = Math.min(height, maxHeight)

  if (side === 'block-end') {
    const startEdge = rtl ? anchor.right - width : anchor.left
    return {
      left: clampToViewport(startEdge, width, viewportWidth),
      top: clampToViewport(anchor.bottom + gap, fitted, viewportHeight),
      maxHeight,
    }
  }

  const endEdge = rtl ? anchor.left - gap - width : anchor.right + gap
  return {
    left: clampToViewport(endEdge, width, viewportWidth),
    top: clampToViewport(anchor.top, fitted, viewportHeight),
    maxHeight,
  }
}
